import {
  type AdminMessage,
  type BaseMessage,
  type FileMessage,
  type UserMessage,
} from "@sendbird/chat/message";

import { HIDDEN_MESSAGE_TYPES, type MessageCustomType } from "../constants";
import {
  type AdminChatMessage,
  type ChatMessage,
  type FileChatMessage,
  type TextChatMessage,
  type WorkplaceUserMetadata,
} from "../types";

/**
 * Parse workplace user metadata from message data string
 */
function parseWorkplaceMetadata(dataString: string): WorkplaceUserMetadata | undefined {
  try {
    const data = JSON.parse(dataString) as WorkplaceUserMetadata;
    if (data.workplaceUserId && data.workplaceUserName) {
      return data;
    }
  } catch {
    // Invalid JSON or missing fields
  }
  return undefined;
}

/**
 * Transform a Sendbird UserMessage to TextChatMessage
 */
function transformUserMessage(message: UserMessage): TextChatMessage {
  const metadata = message.data ? parseWorkplaceMetadata(message.data) : undefined;

  // Helper to check if parent message is a UserMessage with sender
  const isUserMessage = (baseMessage: BaseMessage): baseMessage is UserMessage =>
    baseMessage.isUserMessage();

  return {
    type: "text",
    messageId: message.messageId,
    createdAt: message.createdAt,
    channelUrl: message.channelUrl,
    customType: message.customType,
    senderId: message.sender?.userId,
    senderName: message.sender?.nickname || metadata?.workplaceUserName,
    message: message.message,
    metadata,
    parentMessage:
      message.parentMessage && isUserMessage(message.parentMessage)
        ? {
            messageId: message.parentMessage.messageId,
            message: message.parentMessage.message,
            senderName: message.parentMessage.sender?.nickname,
          }
        : undefined,
  };
}

/**
 * Transform a Sendbird FileMessage to FileChatMessage
 */
function transformFileMessage(message: FileMessage): FileChatMessage {
  const metadata = message.data ? parseWorkplaceMetadata(message.data) : undefined;

  return {
    type: "file",
    messageId: message.messageId,
    createdAt: message.createdAt,
    channelUrl: message.channelUrl,
    customType: message.customType,
    senderId: message.sender?.userId,
    senderName: message.sender?.nickname || metadata?.workplaceUserName,
    fileUrl: message.url,
    fileName: message.name,
    fileType: message.type,
    fileSize: message.size,
    thumbnailUrl: message.thumbnails?.[0]?.url,
    metadata,
  };
}

/**
 * Transform a Sendbird AdminMessage to AdminChatMessage
 */
function transformAdminMessage(message: AdminMessage): AdminChatMessage {
  return {
    type: "admin",
    messageId: message.messageId,
    createdAt: message.createdAt,
    channelUrl: message.channelUrl,
    customType: message.customType,
    message: message.message,
    data: message.data || undefined,
  };
}

/**
 * Transform a Sendbird BaseMessage to the appropriate ChatMessage type
 * Returns null for message types that should be hidden
 */
export function transformSendbirdMessage(message: BaseMessage): ChatMessage | null {
  if (message.isUserMessage()) {
    return transformUserMessage(message);
  }

  if (message.isFileMessage()) {
    return transformFileMessage(message);
  }

  if (message.isAdminMessage()) {
    return transformAdminMessage(message);
  }

  // Unknown message type
  return null;
}

/**
 * Check if a message should be hidden from the workplace view
 */
export function shouldHideMessage(customType: string): boolean {
  return HIDDEN_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

/**
 * Transform an array of Sendbird messages, filtering out null values and hidden messages
 */
export function transformSendbirdMessages(messages: BaseMessage[]): ChatMessage[] {
  return messages
    .map(transformSendbirdMessage)
    .filter((message): message is ChatMessage => message !== null)
    .filter((message) => !shouldHideMessage(message.customType));
}
